/**
 * buildPaperModel — pure helpers producing the preview view-models for the
 * document blocks every PDF export shares (letterhead, patient rows,
 * signature). Mirrors the rules of functions/pdfDocumentBlocks.js so the
 * preview ≈ PDF. Page-specific models (Q&A body, AI text) compose these.
 */

import { formatHebrewDate } from "../../utils/dateUtils";

export const trim = (v) => (typeof v === "string" ? v.trim() : "");

export const ageFrom = (birthDate) => {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age >= 0 && age < 150 ? age : null;
};

export const therapistFullName = (t) =>
  `${t?.firstName || ""} ${t?.lastName || ""}`.trim();

export const patientFullName = (p, fallback = "") =>
  `${p?.firstName || ""} ${p?.lastName || ""}`.trim() || trim(fallback);

/**
 * @param {Object} a
 * @param {Object} a.clinic        clinic profile
 * @param {Object} a.header        options.header
 * @param {string} a.authorName    name shown under the clinic name
 * @param {string} a.licenseNumber
 * @param {string} a.generatedAt   dd/mm/yyyy
 */
export const buildLetterheadModel = ({ clinic, header: h, authorName, licenseNumber, generatedAt }) => {
  const clinicName = trim(clinic?.name) || "CliniKit";
  const letterhead = {
    logoUrl: h.logo && clinic?.logoUrl ? clinic.logoUrl : null,
    logoLarge: !!(h.logo && h.largeLogo && clinic?.logoUrl),
    clinicName: h.clinicName ? clinicName : "",
    authorName: h.therapistName ? authorName : "",
    metaLines: [],
  };
  if (h.clinicAddress && trim(clinic?.clinicAddress)) {
    letterhead.metaLines.push({ text: trim(clinic.clinicAddress) });
  }
  const contact = [];
  if (h.contactPhone && trim(clinic?.contactPhone)) contact.push(trim(clinic.contactPhone));
  if (h.contactEmail && trim(clinic?.contactEmail)) contact.push(trim(clinic.contactEmail));
  if (contact.length) letterhead.metaLines.push({ text: contact.join(" · "), ltr: true });
  if (h.website && trim(clinic?.website)) letterhead.metaLines.push({ text: trim(clinic.website), ltr: true });
  if (h.businessId && trim(clinic?.businessId)) {
    letterhead.metaLines.push({ label: "ח״פ / ע.מ", text: trim(clinic.businessId), bdi: true });
  }
  if (h.license && licenseNumber) {
    letterhead.metaLines.push({ label: "מס' רישיון", text: licenseNumber, bdi: true });
  }
  letterhead.metaLines.push({ label: "תאריך הפקה", text: generatedAt, bdi: true });
  return letterhead;
};

/**
 * Patient block rows. `dateValue`/`dateLabel` let the caller decide what the
 * "date" row means (single treatment date vs. sessions range).
 */
export const buildPatientRows = ({
  patientToggles: p,
  patientName,
  patientDoc,
  dateLabel,
  dateValue,
  typeName,
  authorName,
}) => {
  const rows = [];
  if (p.name && patientName) rows.push({ label: "שם המטופל/ת", value: patientName });
  if (p.treatmentDate && dateValue) rows.push({ label: dateLabel, value: dateValue, bdi: true });
  if (p.treatmentType && typeName) rows.push({ label: "סוג הטיפול", value: typeName });
  if (authorName) rows.push({ label: "מטפל/ת", value: authorName });
  if (p.idNumber && trim(patientDoc?.idNumber)) {
    rows.push({ label: "ת.ז.", value: trim(patientDoc.idNumber), bdi: true });
  }
  if (p.birthDate && patientDoc?.birthDate) {
    const dob = formatHebrewDate(patientDoc.birthDate);
    const age = ageFrom(patientDoc.birthDate);
    if (dob) rows.push({ label: "תאריך לידה", value: age != null ? `${dob} (גיל ${age})` : dob, bdi: true });
  }
  if (p.phone && trim(patientDoc?.phone)) {
    rows.push({ label: "טלפון", value: trim(patientDoc.phone), bdi: true });
  }
  return rows;
};

export const buildSignatureModel = ({ therapist, clinic, signatureMode, licenseNumber, generatedAt }) => ({
  mode: signatureMode,
  imageUrl: signatureMode === "saved" ? therapist?.signature?.dataUrl || null : null,
  exporterName: therapistFullName(therapist),
  licenseNumber,
  generatedAt,
  clinicName: trim(clinic?.name) || "CliniKit",
});

export const resolveLicenseNumber = (therapist, clinic) =>
  trim(String(therapist?.licenseNumber || clinic?.licenseNumber || ""));
